/**
 * The one mapping between the two presence vocabularies, shared by the web
 * client and the edge function. The client vocabulary (types/presence.ts) is
 * canonical: it is what people pick, what is stored in manual_presence_status
 * and what the UI renders. The legacy vocabulary is what /presence/users has
 * always returned in `status` and what older clients may still have written to
 * the column, so it is still read and still emitted alongside the canonical
 * `state`.
 */
import type { ManualPresenceEntry, PresenceStatus, ResolvedPresenceStatus } from '../types/presence.ts';

export type LegacyPresenceStatus =
  | 'online'
  | 'in_meeting'
  | 'out_of_office'
  | 'away'
  | 'commuting'
  | 'off_sick'
  | 'on_holiday'
  | 'working_remotely'
  | 'not_connected';

export const MANUAL_PRESENCE_STATUSES: readonly PresenceStatus[] = [
  'available', 'busy', 'dnd', 'be_right_back', 'away', 'offline',
];

export const RESOLVED_PRESENCE_STATUSES: readonly ResolvedPresenceStatus[] = [
  ...MANUAL_PRESENCE_STATUSES, 'in_meeting', 'on_site',
];

/**
 * Canonical to legacy. Lossy by design: the legacy set has no Do Not Disturb,
 * Be Right Back or On site, so those fall to the nearest legacy colour and the
 * label (sent alongside) keeps the precise wording for older clients.
 */
const CANONICAL_TO_LEGACY: Record<ResolvedPresenceStatus, LegacyPresenceStatus> = {
  available:     'online',
  busy:          'in_meeting',
  dnd:           'in_meeting',
  be_right_back: 'away',
  away:          'away',
  // Appear Offline must hide the person from older clients too, and
  // not_connected is the only legacy value they draw no dot for.
  offline:       'not_connected',
  in_meeting:    'in_meeting',
  on_site:       'online',
};

/** Legacy to canonical, for column values written by older clients. */
const LEGACY_TO_CANONICAL: Record<Exclude<LegacyPresenceStatus, 'not_connected'>, ResolvedPresenceStatus> = {
  online:           'available',
  in_meeting:       'in_meeting',
  out_of_office:    'away',
  away:             'away',
  commuting:        'be_right_back',
  off_sick:         'away',
  on_holiday:       'away',
  working_remotely: 'available',
};

/** Labels other people see. Sentence case to match the rest of the presence copy. */
export const PRESENCE_STATE_LABELS: Record<ResolvedPresenceStatus, string> = {
  available:     'Available',
  busy:          'Busy',
  dnd:           'Do not disturb',
  be_right_back: 'Be right back',
  away:          'Away',
  offline:       'Offline',
  in_meeting:    'In a meeting',
  on_site:       'On site',
};

export function isResolvedPresenceStatus(value: unknown): value is ResolvedPresenceStatus {
  return typeof value === 'string' && (RESOLVED_PRESENCE_STATUSES as readonly string[]).includes(value);
}

/**
 * Out of office has no canonical word of its own (it is shown as away with its
 * own label), so the source decides it: older clients always got
 * `out_of_office` for it and colour it differently from plain away.
 */
export function toLegacyPresenceStatus(state: ResolvedPresenceStatus | null, source?: string): LegacyPresenceStatus {
  if (state && source === 'out_of_office') return 'out_of_office';
  return state ? CANONICAL_TO_LEGACY[state] : 'not_connected';
}

/**
 * The status to colour a presence by: the canonical state, except out of
 * office, which keeps its own legacy colour.
 */
export function presenceColourStatus(
  presence: { status?: LegacyPresenceStatus; state?: ResolvedPresenceStatus | null } | null | undefined,
): ResolvedPresenceStatus | LegacyPresenceStatus | null {
  if (!presence) return null;
  if (presence.status === 'out_of_office') return 'out_of_office';
  return presence.state !== undefined ? presence.state : presence.status ?? null;
}

export function fromLegacyPresenceStatus(value: unknown): ResolvedPresenceStatus | null {
  if (typeof value !== 'string' || value === 'not_connected') return null;
  return (LEGACY_TO_CANONICAL as Record<string, ResolvedPresenceStatus>)[value] ?? null;
}

/**
 * Reads a stored manual_presence_status in either vocabulary. Canonical values
 * win where a word exists in both (only `away` and `in_meeting` do, and they
 * mean the same thing in each).
 */
export function normaliseStoredPresenceStatus(value: unknown): ResolvedPresenceStatus | null {
  if (isResolvedPresenceStatus(value)) return value;
  return fromLegacyPresenceStatus(value);
}

export interface ManualPresenceColumns {
  manualPresenceStatus: PresenceStatus | null;
  manualPresenceLabel: string | null;
  manualPresenceUntil: string | null;
  manualPresenceClearMode: 'dont_clear' | 'custom' | null;
  manualPresenceSetAt: string | null;
}

/**
 * The manual_presence_* column values for a client manual entry, so other
 * people's /presence/users reads see what this person picked. An already
 * expired entry clears the columns rather than storing a status nobody shows.
 */
export function manualEntryToColumns(entry: ManualPresenceEntry | null | undefined, now: Date = new Date()): ManualPresenceColumns {
  const expired = entry?.expiresAt ? new Date(entry.expiresAt).getTime() <= now.getTime() : false;
  if (!entry || expired || !(MANUAL_PRESENCE_STATUSES as readonly string[]).includes(entry.status)) {
    return {
      manualPresenceStatus: null,
      manualPresenceLabel: null,
      manualPresenceUntil: null,
      manualPresenceClearMode: null,
      manualPresenceSetAt: null,
    };
  }
  return {
    manualPresenceStatus: entry.status,
    manualPresenceLabel: PRESENCE_STATE_LABELS[entry.status],
    manualPresenceUntil: entry.expiresAt,
    manualPresenceClearMode: entry.expiresAt ? 'custom' : 'dont_clear',
    manualPresenceSetAt: entry.setAt,
  };
}
